import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import CTASection  from '../components/CTASection';
import Reveal      from '../components/Reveal';

const PRINCIPLES = [
  {
    num:   '01',
    title: 'Long-Term Ownership Horizon',
    body:  'We measure our performance in years, not quarters. Stewardship decisions are made with the long arc of brand health and value creation in mind, not short-term optimization.',
  },
  {
    num:   '02',
    title: 'Operational Transparency',
    body:  'Every brand in our portfolio operates within a visible, documented framework. Reporting, processes, and performance expectations are clearly defined and consistently maintained.',
  },
  {
    num:   '03',
    title: 'Governance Before Scale',
    body:  'We believe that governance infrastructure must precede scale — not follow it. Systems, controls, and oversight mechanisms are in place before growth creates the pressure that exposes their absence.',
  },
  {
    num:   '04',
    title: 'Capital Discipline',
    body:  'Every material investment in the portfolio is subject to structured evaluation. Return expectations are clearly articulated, capital is deployed with intention, and performance is tracked against original underwriting assumptions.',
  },
  {
    num:   '05',
    title: 'Risk Awareness',
    body:  'Operational, legal, market, and reputational risks are actively identified and managed across all portfolio brands. We do not wait for risk to surface — we build systems to surface it first.',
  },
  {
    num:   '06',
    title: 'Sustainable Value Creation',
    body:  'We build brands that can stand on their own over time. Our focus is on durable fundamentals, loyal audiences, and operating efficiency — not growth-at-any-cost strategies that compromise long-term health.',
  },
];

const DECISION_FRAMEWORK = [
  {
    q: 'Does this align with our long-term stewardship mandate?',
    a: 'Every major decision is evaluated against our stated ownership horizon. Short-term wins that compromise long-term brand equity are systematically deprioritized.',
  },
  {
    q: 'Is it governed by adequate controls and oversight?',
    a: 'Material operational and financial decisions require documented process, appropriate approval, and clear accountability before implementation.',
  },
  {
    q: 'Is the risk understood and manageable?',
    a: 'Known risks are documented. Unknown risks are investigated. We do not proceed on the assumption that nothing will go wrong — we plan for when something does.',
  },
  {
    q: 'Does it create value across the portfolio?',
    a: 'We look for decisions that compound — where value created in one brand or function strengthens the broader portfolio through shared infrastructure, knowledge, or efficiency.',
  },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Driven 2 LLC — Our Philosophy and Governance Approach</title>
        <meta
          name="description"
          content="Driven 2 LLC is a disciplined holding company and owner-operator of digital brands. Learn about our mission, core principles, and long-term governance philosophy."
        />
        <meta property="og:title" content="About Driven 2 LLC — Our Philosophy and Governance Approach" />
        <meta property="og:description" content="A disciplined holding company built on long-term stewardship, transparent governance, and capital discipline. Learn who we are and how we operate." />
        <meta property="og:url" content="https://www.driven2.net/about" />
        <link rel="canonical" href="https://www.driven2.net/about" />
      </Helmet>

      {/*
        ═══════════════════════════════════════════
        HERO PLACEHOLDER DIRECTION:
        "Refined corporate leadership image —
        executive boardroom with glass-wall
        conference room, muted architecture,
        strategic planning documents visible,
        understated luxury business atmosphere.
        Dark overlay, light text."

        TO REPLACE: <HeroSection bgImage="/images/hero-about.jpg" ... />
        ═══════════════════════════════════════════
      */}
      <HeroSection
        eyebrow="About Driven 2 LLC"
        title={"Principled Stewardship.\nProfessional Discipline."}
        subtitle="We are a private holding company defined not by what we own, but by how we own it — with rigor, transparency, and an unwavering commitment to long-term value creation."
        size="medium"
        bgImage="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80"
      />

      {/* ═══════════════════════════════════════
          MISSION
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div style={{ maxWidth: '880px', margin: '0 auto', textAlign: 'center' }}>
            <Reveal>
              <div className="eyebrow eyebrow--center">Mission</div>
              <blockquote
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 400,
                  color: 'var(--navy-800)',
                  lineHeight: 1.45,
                  fontStyle: 'normal',
                  borderLeft: 'none',
                  padding: 0,
                }}
              >
                "To build a portfolio of digital brands that endures — through disciplined
                ownership, transparent governance, and the institutional maturity to
                create lasting value for the businesses we steward and the partners we serve."
              </blockquote>
              <div style={{ margin: 'var(--sp-8) auto 0', width: '4rem', height: '1px', background: 'linear-gradient(90deg, transparent, var(--gold-500), transparent)' }} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          WHO WE ARE
          ═══════════════════════════════════════ */}
      <section className="section section--alt">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>

            <Reveal>
              <div className="eyebrow">The Company</div>
              <h2 className="d-lg t-navy mb-8">Owner-Operator, Not Fund Manager</h2>
              <p className="lead mb-6">
                Driven 2 LLC is not a venture fund. We do not deploy capital into dozens
                of early-stage bets and walk away. We are an owner-operator — a holding
                company that takes a hands-on, long-horizon approach to the brands we
                build and acquire.
              </p>
              <p className="lead mb-6">
                Based in Florida, we operate a focused portfolio of
                digital brands across adjacent sectors. Each brand receives centralized
                governance support, shared services infrastructure, and the benefit of
                institutional-grade oversight that standalone operators rarely achieve.
              </p>
              <p className="lead">
                Our size is intentional. We believe focused portfolios with deep operational
                engagement outperform sprawling ones with diluted attention. Quality of
                oversight is not scalable through volume — it requires proximity,
                accountability, and time.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)' }}>
                {[
                  { label: 'Structure',    value: 'Private Holding Company (LLC)' },
                  { label: 'Portfolio',    value: 'Digital Brand Portfolio' },
                  { label: 'Approach',     value: 'Owner-Operator / Long-Term Steward' },
                  { label: 'Focus',        value: 'Digital Commerce · AI Technology · Commerce · Performance' },
                  { label: 'Governance',   value: 'Centralized, Documented, Audit-Ready' },
                  { label: 'Contact',      value: 'info@driven2.net · 904-638-7199' },
                ].map(({ label, value }) => (
                  <div key={label} className="contact-info-item">
                    <div className="contact-info-label">{label}</div>
                    <div className="contact-info-value">{value}</div>
                  </div>
                ))}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          CORE PRINCIPLES
          ═══════════════════════════════════════ */}
      <section className="section section--dark">
        <div className="container">
          <Reveal>
            <div className="section-intro section-intro--center">
              <div className="eyebrow eyebrow--center eyebrow--light">Core Principles</div>
              <h2 className="d-lg t-white">The Standards That Define How We Operate</h2>
              <p className="lead lead--white" style={{ maxWidth: '580px', margin: '0 auto' }}>
                These are not aspirational values. They are operational standards — tested,
                documented, and actively enforced across every brand in our portfolio.
              </p>
            </div>
          </Reveal>

          <div className="grid-3" style={{ marginTop: 'var(--sp-12)' }}>
            {PRINCIPLES.map(({ num, title, body }, i) => (
              <Reveal key={num} delay={i * 0.07}>
                <div className="card card--dark" style={{ height: '100%' }}>
                  <div className="card__num">{num}</div>
                  <h3 className="card__title">{title}</h3>
                  <p className="card__body">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          DECISION FRAMEWORK
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>

            <Reveal>
              <div className="eyebrow">Decision Framework</div>
              <h2 className="d-lg t-navy">How We Make Material Decisions</h2>
              <p className="lead" style={{ marginTop: 'var(--sp-6)' }}>
                Every significant operational, financial, or strategic decision at Driven 2
                LLC is evaluated against a consistent framework. Consistency of process
                produces consistency of outcomes.
              </p>
            </Reveal>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {DECISION_FRAMEWORK.map(({ q, a }, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div style={{ padding: 'var(--sp-8) 0', borderBottom: '1px solid var(--gray-200)' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', fontWeight: 500, color: 'var(--navy-800)', marginBottom: 'var(--sp-3)' }}>
                      {q}
                    </div>
                    <p className="lead" style={{ color: 'var(--gray-600)', marginBottom: 0 }}>{a}</p>
                  </div>
                </Reveal>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          PARTNERSHIP MINDSET
          ═══════════════════════════════════════ */}
      <section className="section section--alt">
        <div className="container">
          <Reveal>
            <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
              <div className="eyebrow eyebrow--center">Partnership Mindset</div>
              <h2 className="d-lg t-navy mb-8">
                We Work Best with Counterparts Who Think Long-Term
              </h2>
              <p className="lead mb-6">
                The most productive relationships we build are with people and organizations
                that share our orientation toward disciplined, long-term thinking. Whether
                that's a brand founder looking for the right home for what they've built,
                an investor evaluating portfolio-level governance, or a service partner
                seeking a well-organized operational counterpart — we value substance,
                clarity, and alignment of expectations above all else.
              </p>
              <p className="lead">
                We are transparent about how we operate, what we can offer, and what we
                expect. If that directness sounds like the kind of partnership you are
                looking for, we would welcome a conversation.
              </p>
              <div style={{ marginTop: 'var(--sp-10)', display: 'flex', gap: 'var(--sp-4)', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-primary">Begin a Conversation</Link>
                <Link to="/brands" className="btn btn-outline-navy">View Portfolio</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        eyebrow="Get in Touch"
        headline="Serious Inquiries Welcome"
        subtext="For partnership, investment, or acquisition discussions, reach out directly. We review all inquiries carefully and respond to substantive ones promptly."
        primaryCTA={{ label: 'Contact Driven 2 LLC', to: '/contact' }}
        secondaryCTA={{ label: 'Explore Operating Model', to: '/operating-model' }}
        variant="dark"
      />
    </>
  );
}
